import axios from 'axios';

/**
 * The mailbox API client, for any service that mounts mailbox_core's routes
 * (service-inbox's `mailbox_core.create_mailbox_router`) under `base`.
 *
 * Knows nothing about a client: a client's own list filters arrive as extra
 * keys in `query` and go out as query parameters as they are.
 */

// A key repeated for each value of an array (category=a&category=b), which the
// service reads as a list; axios's default would send category[]=a.
const toSearch = (query) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value == null || value === '') return;
    [].concat(value).forEach((v) => {
      if (v == null || v === '') return;
      params.append(key, v instanceof Date ? v.toISOString() : String(v));
    });
  });
  return params;
};

export const createMailboxApi = (base) => ({
  /** `{ mailbox, folders }`: the top-level folders. */
  getFolders: async () => (await axios.get(`${base}/folders`)).data,

  getFolderChildren: async (folderId) =>
    (await axios.get(`${base}/folders/${encodeURIComponent(folderId)}/children`)).data,

  /**
   * One page of a folder. `query` holds the mailbox's own filters (`q`, `since`,
   * `until` as Dates) and any a client's plugin adds.
   */
  getFolderMessages: async (folderId, { limit = 50, offset = 0, query = {} } = {}) => {
    const params = toSearch({ ...query, q: (query.q || '').trim() || undefined });
    params.set('limit', limit);
    params.set('offset', offset);
    return (
      await axios.get(`${base}/folders/${encodeURIComponent(folderId)}/messages`, { params })
    ).data;
  },

  /**
   * Search every folder (or `folderId`) through the mail provider's index:
   * `{ query, messages, next_cursor }`, newest first, each message with its
   * `folder_name`. `cursor` is the previous page's `next_cursor`.
   */
  search: async (q, { cursor, folderId, limit = 50, flagged = false } = {}) =>
    (
      await axios.get(`${base}/search`, {
        params: {
          q: q || undefined,
          cursor: cursor || undefined,
          folder_id: folderId || undefined,
          flagged: flagged || undefined,
          limit,
        },
      })
    ).data,

  /** Flag a message for follow-up, mark it complete, or clear it: `flagged` | `complete` | `none`. */
  setFlag: async (messageId, flag) =>
    (await axios.post(`${base}/messages/${encodeURIComponent(messageId)}/flag`, { flag })).data,

  /** One message in full, body included. Reading it does not mark it read; `setRead` does. */
  getMessage: async (messageId) =>
    (await axios.get(`${base}/messages/${encodeURIComponent(messageId)}`)).data,

  /**
   * One attachment's content, as a Blob. The service serves it as a download
   * typed by its bytes; save it, never open it (see Attachments.js).
   */
  getAttachment: async (messageId, attachmentId) =>
    (
      await axios.get(
        `${base}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { responseType: 'blob' }
      )
    ).data,

  /**
   * Mark a message read or unread in the mailbox itself, recorded against the
   * signed-in person. Only where the service mounts the write routes.
   */
  setRead: async (messageId, isRead) =>
    (await axios.post(`${base}/messages/${encodeURIComponent(messageId)}/read`, { is_read: isRead }))
      .data,

  /**
   * Move a message to a folder, by id (or `archive`): `{ id, folder_id,
   * from_folder_id }`. The message has a **new id** after a move; Undo is the
   * same call with that id and `from_folder_id`.
   */
  move: async (messageId, destinationId) =>
    (
      await axios.post(`${base}/messages/${encodeURIComponent(messageId)}/move`, {
        destination_id: destinationId,
      })
    ).data,

  /**
   * Who did what to a message from the dashboard, newest first, by its
   * RFC-822 Message-ID: `[{ action, actor, at, from_folder_id, to_folder_id }]`.
   */
  history: async (internetMessageId) =>
    (await axios.get(`${base}/history`, { params: { internet_message_id: internetMessageId } }))
      .data,

  // -- the composer: drafts in the mailbox's Drafts folder, sent at most once --

  /** Start a draft: `kind` is reply, reply_all, forward (of `messageId`) or new. */
  createDraft: async (kind, messageId) =>
    (await axios.post(`${base}/drafts`, { kind, message_id: messageId || undefined })).data,

  /** People to write to, matching what was typed: `[{ name, address, detail }]`. */
  suggestAddresses: async (q, limit = 8) =>
    (await axios.get(`${base}/addresses`, { params: { q, limit } })).data,

  getDraft: async (draftId) =>
    (await axios.get(`${base}/drafts/${encodeURIComponent(draftId)}`)).data,

  /** Save what changed: any of `subject`, `to`, `cc`, `bcc` (address lists), `text`. */
  saveDraft: async (draftId, changes) =>
    (await axios.patch(`${base}/drafts/${encodeURIComponent(draftId)}`, changes)).data,

  discardDraft: async (draftId) => {
    await axios.delete(`${base}/drafts/${encodeURIComponent(draftId)}`);
  },

  /** A draft's files: `[{ id, name, size, inline, ... }]`, a forward's own included. */
  listDraftAttachments: async (draftId) =>
    (await axios.get(`${base}/drafts/${encodeURIComponent(draftId)}/attachments`)).data,

  /**
   * Attach one File to a draft: its bytes as the body, its name in the query.
   * Answers with the draft's files as they now are.
   */
  addDraftAttachment: async (draftId, file) =>
    (
      await axios.post(`${base}/drafts/${encodeURIComponent(draftId)}/attachments`, file, {
        params: { name: file.name },
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
    ).data,

  removeDraftAttachment: async (draftId, attachmentId) => {
    await axios.delete(
      `${base}/drafts/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(attachmentId)}`
    );
  },

  /** Send a draft. Refused (409) if it was already sent, or may have been. */
  sendDraft: async (draftId) =>
    (await axios.post(`${base}/drafts/${encodeURIComponent(draftId)}/send`)).data,

  /**
   * The client's figures over everything in a folder (its newest few thousand):
   * `{ folder_id, scanned, complete, data }`, `data` drawn by the client's plugin.
   */
  /**
   * The plugin's figures over a folder, under the same `query` as the list
   * (`getFolderMessages`), so the two agree.
   */
  folderBreakdown: async (folderId, query = {}) => {
    const params = toSearch({ ...query, q: (query.q || '').trim() || undefined });
    return (
      await axios.get(`${base}/folders/${encodeURIComponent(folderId)}/breakdown`, { params })
    ).data;
  },

  /** A conversation's messages, oldest first, each located by Message-ID. */
  getConversation: async (conversationId) =>
    (await axios.get(`${base}/conversation`, { params: { conversation_id: conversationId } }))
      .data,

  /** Where a message is now, by its RFC-822 Message-ID: `{ id, folder_id }`. */
  locate: async (internetMessageId) =>
    (await axios.get(`${base}/locate`, { params: { internet_message_id: internetMessageId } }))
      .data,
});

/** A request error's message for a person: the service's `detail`, else `fallback`. */
export const apiMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};
